import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  FileText,
  Bookmark,
  CreditCard,
  LifeBuoy,
  ArrowRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

function deriveAssessmentCard(asmt) {
  if (!asmt || asmt.status === "draft") {
    const hasAnyAnswer =
      asmt &&
      (asmt.current_situation?.employment_status ||
        (asmt.skills || []).length > 0 ||
        (asmt.work_history || []).length > 0);
    if (hasAnyAnswer) {
      return {
        status: "In progress",
        cta: "Continue assessment",
        href: "/assessment",
      };
    }
    return { status: "Not started", cta: "Start assessment", href: "/assessment" };
  }
  if (asmt.status === "submitted") {
    return { status: "Submitted", cta: "View review", href: "/assessment/review" };
  }
  if (asmt.status === "processed") {
    return { status: "Report ready", cta: "View review", href: "/assessment/review" };
  }
  return { status: "Not started", cta: "Start assessment", href: "/assessment" };
}

function deriveReportCard(asmt) {
  if (asmt?.ai_recommendation?.pathways) {
    return { status: "Ready", cta: "View report", href: "/career-report" };
  }
  if (asmt?.status === "submitted" || asmt?.status === "processed") {
    return { status: "Generate now", cta: "Generate report", href: "/career-report" };
  }
  return { status: "Locked", cta: "Take the assessment", href: "/assessment" };
}

const CARDS = [
  {
    icon: ClipboardList,
    title: "My Assessment",
    body: "Complete a short questionnaire so we can understand your situation and tailor your plan.",
    cta: "Start assessment",
    href: "/assessment",
    accent: "from-sky-50 to-white",
    iconBg: "bg-sky-100 text-sky-700",
    testid: "dashboard-card-assessment",
    status: "Not started",
  },
  {
    icon: FileText,
    title: "My Career Report",
    body: "Your personalised report — career directions, skills to build, and roles to consider.",
    cta: "View when ready",
    href: "#career-report",
    accent: "from-emerald-50 to-white",
    iconBg: "bg-emerald-100 text-emerald-700",
    testid: "dashboard-card-career-report",
    status: "Locked",
  },
  {
    icon: Bookmark,
    title: "Saved Jobs",
    body: "Bookmark roles and listings you want to revisit. We'll keep them organised here.",
    cta: "Browse jobs",
    href: "/jobs",
    accent: "from-amber-50 to-white",
    iconBg: "bg-amber-100 text-amber-700",
    testid: "dashboard-card-saved-jobs",
    status: "0 saved",
  },
  {
    icon: CreditCard,
    title: "Subscription",
    body: "Career Reset membership — $50/month, minimum 3 months. Manage your plan here.",
    cta: "Manage plan",
    href: "/subscribe",
    accent: "from-violet-50 to-white",
    iconBg: "bg-violet-100 text-violet-700",
    testid: "dashboard-card-subscription",
    status: "Trial",
  },
  {
    icon: LifeBuoy,
    title: "Support Options",
    body: "Get unstuck. Browse guides, contact support, or talk to a human coach when you need one.",
    cta: "Get support",
    href: "#support",
    accent: "from-rose-50 to-white",
    iconBg: "bg-rose-100 text-rose-700",
    testid: "dashboard-card-support",
    status: "Available",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const firstName = (user?.name || user?.email || "there").split(/[ @]/)[0];
  const [assessment, setAssessment] = useState(null);
  const [assessmentLoaded, setAssessmentLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/assessment/me");
        if (active) setAssessment(data);
      } catch {
        if (active) setAssessment(null);
      } finally {
        if (active) setAssessmentLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const asmtCard = deriveAssessmentCard(assessment);
  const reportCard = deriveReportCard(assessment);
  const loadingAssessment = !assessmentLoaded;
  const dynamicCards = CARDS.map((c) => {
    if (c.testid === "dashboard-card-assessment")
      return loadingAssessment ? { ...c, status: "…" } : { ...c, ...asmtCard };
    if (c.testid === "dashboard-card-career-report")
      return loadingAssessment ? { ...c, status: "…" } : { ...c, ...reportCard };
    return c;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col gap-2">
            <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
              Dashboard
            </p>
            <h1
              className="text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
              data-testid="dashboard-heading"
            >
              {`Welcome back, ${firstName}.`}
            </h1>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl">
              {"Here's your home base. Take the next small step whenever you're ready — nothing here is graded."}
            </p>
          </div>

          <div
            className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
            data-testid="dashboard-cards-grid"
          >
            {dynamicCards.map((card) => (
              <article
                key={card.title}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br ${card.accent} p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300`}
                data-testid={card.testid}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${card.iconBg}`}
                  >
                    <card.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-1">
                    {card.status}
                  </span>
                </div>

                <h2 className="mt-6 text-xl font-medium text-slate-900">
                  {card.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {card.body}
                </p>

                {card.href.startsWith("/") ? (
                  <Link
                    to={card.href}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 hover:gap-2 transition-all"
                    data-testid={`${card.testid}-cta`}
                  >
                    {card.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a
                    href={card.href}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 hover:gap-2 transition-all"
                    data-testid={`${card.testid}-cta`}
                  >
                    {card.cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </article>
            ))}

            <article
              className="hidden xl:flex flex-col justify-center rounded-2xl border border-dashed border-slate-300 bg-white/50 p-7 text-center"
              data-testid="dashboard-card-tip"
            >
              <p className="text-sm uppercase tracking-[0.18em] text-slate-400 font-medium">
                Tip
              </p>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">
                {"Career change is a series of small, doable steps. We'll keep nudging you with the next one."}
              </p>
              <Link
                to="/account"
                className="mt-6 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-slate-900 hover:gap-2 transition-all"
                data-testid="dashboard-account-link"
              >
                Visit your account
                <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}
